import { useEffect, useRef, useState, RefObject } from "react";

export type UseIntersectionObserverProps<T extends HTMLElement = HTMLElement> =
  IntersectionObserverInit & {
    containerReference?: T;
    observableItems?: Array<Element>;
    callback?: (entries: IntersectionObserverEntry[]) => void;
  };

export function useIntersectionObserver<T extends HTMLElement = HTMLElement>(
  options: UseIntersectionObserverProps<T>,
): [RefObject<T>, boolean] {
  const {
    root,
    rootMargin,
    threshold,
    containerReference,
    observableItems,
    callback,
  } = options;
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<T>(containerReference || null);

  const callbackFunction = (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setIsVisible(entry?.isIntersecting ?? false);
  };

  useEffect(() => {
    if (containerRef) {
      const observer = new IntersectionObserver(callback ?? callbackFunction, {
        root: root,
        rootMargin: rootMargin || "0px",
        threshold: threshold || 0,
      });

      if (observableItems) {
        observableItems.forEach((item) => {
          if (item) observer.observe(item);
        });

        return () => {
          observableItems.forEach((item) => {
            if (item) observer.unobserve(item);
          });
        };
      } else {
        if (containerRef.current) {
          const currentContainer = containerRef.current;
          observer.observe(currentContainer);
          return () => {
            observer.unobserve(currentContainer);
          };
        }
      }
    }
  }, [containerRef, root, rootMargin, threshold, observableItems, callback]);

  return [containerRef, isVisible];
}
